import "./utils/appInsights";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./i18n"; // Import the i18n configuration
import { suppressResizeObserverError } from "./utils/suppressResizeObserverError";

// Suppress ResizeObserver errors before anything else
suppressResizeObserverError();

// Disable console logging in production
if (import.meta.env.PROD) {
  const noop = () => {};
  console.log = noop;
  console.error = noop;
  console.warn = noop;
  console.info = noop;
  console.debug = noop;
  console.trace = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.groupCollapsed = noop;
  console.table = noop;
  console.time = noop;
  console.timeEnd = noop;
  console.assert = noop;
  console.dir = noop;
}

// Recover from chunk load failures after redeployment (stale cached pages reference old hashes).
// Reload once per session to pick up the new assets; sessionStorage flag prevents infinite loops.
const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

function isChunkLoadError(message: string): boolean {
  return (
    message.includes("dynamically imported module") ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk") ||
    message.includes("Failed to fetch dynamically imported module")
  );
}

function handleChunkLoadError(): void {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  }
}

// Suppress ResizeObserver errors (common with Chart.js and other responsive components)
// Also catch chunk load failures from stale cached pages after redeployment.
window.addEventListener("error", (e) => {
  if (
    e.message &&
    (e.message.includes(
      "ResizeObserver loop completed with undelivered notifications",
    ) ||
      e.message.includes("ResizeObserver loop limit exceeded"))
  ) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return true;
  }

  if (e.message && isChunkLoadError(e.message)) {
    handleChunkLoadError();
  }
});

// Dynamic imports (lazy routes) reject as unhandled promises, not window errors.
window.addEventListener("unhandledrejection", (e) => {
  const message =
    e.reason instanceof Error ? e.reason.message : String(e.reason ?? "");
  if (isChunkLoadError(message)) {
    handleChunkLoadError();
  }
});

// Method 2: Override ResizeObserver with debounced version
const debounce = <T extends unknown[]>(
  callback: (...args: T) => void,
  delay: number,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return (...args: T) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => callback(...args), delay);
  };
};

// Create a patched ResizeObserver that debounces notifications
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class DebouncedResizeObserver {
  private observer: InstanceType<typeof OriginalResizeObserver>;

  constructor(callback: ResizeObserverCallback) {
    const debouncedCallback = debounce<[ResizeObserverEntry[], ResizeObserver]>(
      (entries, observer) => {
        callback(entries, observer);
      },
      20,
    );

    this.observer = new OriginalResizeObserver(debouncedCallback);
  }

  observe(target: Element, options?: ResizeObserverOptions) {
    this.observer.observe(target, options);
  }

  unobserve(target: Element) {
    this.observer.unobserve(target);
  }

  disconnect() {
    this.observer.disconnect();
  }
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  // <React.StrictMode> {/* Temporarily commented out for debugging SignalR double count */}
  //<Suspense fallback={<div>Loading translations...</div>}> {/* Or any other fallback UI */}
  <App />,
  //</Suspense>
  // </React.StrictMode> {/* Temporarily commented out for debugging SignalR double count */}
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
